import React from "react";
import styled from "styled-components";

import HelpIcon from "components/svg/help.svg";

/**
 * Usage:
 *
 *     <Tooltip
 *       text={["First line", "Second line"]}
 *       position="bottom"
 *       backgroundColor="#fff"
 *     >
 *       <HelpIcon />
 *     </Tooltip>
 *
 * If `backgroundColor` is provided, it will override the default (yellow).
 * If `primaryTip` is true, it will use slightly larger text with more padding,
 * and the background color (if not specified) will default to white.
 */

// Helper to determine tip color based on props
const tipColorFor = p =>
  p.backgroundColor || // you can specify the tip color manually
  (p.primaryTip // or use the default color…
    ? p.theme.content.backgroundColor // …which is white for a `primaryTip`
    : p.theme.Tooltip.backgroundColor); // …or light yellow for generic tooltips

export const TooltipContainer = styled.div`
  background: ${p => tipColorFor(p)};
  border-radius: 1.5px;
  color: ${p => p.color || p.theme.Tooltip.color};
  -webkit-filter: drop-shadow(0 1px 4px rgba(0, 0, 0, 0.2));
  filter: drop-shadow(0 1px 4px rgba(0, 0, 0, 0.2));
  position: absolute;
  white-space: nowrap;
  z-index: 2;

  &:after {
    border: 5px solid transparent;
    content: " ";
    display: block;
    position: absolute;
  }

  /* Placement */
  ${p => {
    const tipColor = tipColorFor(p);
    switch (p.position) {
      case "top":
        return `
          top: -10px;
          left: 50%;
          transform: translate(-50%, -100%);

          &:after {
            transform: translateX(-50%);
            left: 50%;
            top: 100%;
            border-top-color: ${tipColor};
          }
        `;
      case "top left":
        return `
          top: -10px;
          left: 1px;
          transform: translate(0, -100%);

          &:after {
            transform: translateX(-50%);
            left: 14px;
            top: 100%;
            border-top-color: ${tipColor};
          }
        `;
      case "top right":
        return `
          top: -10px;
          right: 1px;
          transform: translate(0, -100%);

          &:after {
            transform: translateX(-50%);
            right: 14px;
            top: 100%;
            border-top-color: ${tipColor};
          }
        `;
      case "bottom right":
        return `
          bottom: -10px;
          right: 1px;
          transform: translateY(100%);

          &:after {
            right: 14px;
            top: -10px;
            border-bottom-color: ${tipColor};
          }
        `;
      case "bottom right offset":
        return `
          bottom: -5px;
          right: -10px;
          transform: translateY(100%);

          &:after {
            right: 14px;
            top: -10px;
            border-bottom-color: ${tipColor};
          }
        `;
      case "bottom left offset":
        return `
            bottom: -7px;
            left: 150%;
            transform: translate(-50%, 100%);
  
            &:after {
              transform: translateX(-50%);
              left: 50%;
              top: -10px;
              border-bottom-color: ${tipColor};
            }
          `;
      case "bottom":
      default:
        return `
          bottom: -10px;
          left: 50%;
          transform: translate(-50%, 100%);

          &:after {
            transform: translateX(-50%);
            left: 50%;
            top: -10px;
            border-bottom-color: ${tipColor};
          }
        `;
    }
  }};
`;

const Tip = styled(TooltipContainer.withComponent("span"))`
  font-size: 12px;
  font-weight: normal;
  line-height: 1.5em;
  padding: 5px;

  span {
    display: block;
  }
`;

const PrimaryTip = styled(Tip)`
  font-size: 13px;
  line-height: 22px;
  padding: 15px;
`;

export default class Tooltip extends React.Component {
  state = {
    hover: false,
    active: false,
  };

  static getDerivedStateFromProps(props, state) {
    if (props.active) {
      return {
        active: true,
        hover: false,
      };
    } else if (state.active) {
      return { active: false };
    }
    return null;
  }

  onMouseEnter = () => {
    this.setState({
      active: true,
      hover: true,
    });
  };

  onMouseLeave = () => {
    this.setState({ hover: false });
  };

  render() {
    const {
      children,
      text,
      position,
      backgroundColor,
      color,
      primaryTip,
      customContent,
    } = this.props;
    const { active, hover } = this.state;
    const TipContainer = customContent
      ? TooltipContainer
      : primaryTip ? PrimaryTip : Tip;
    const visible = (text || customContent) && (active || hover);
    const Wrapper = customContent ? "div" : "span";
    return (
      <Wrapper
        onMouseEnter={this.onMouseEnter}
        onMouseLeave={this.onMouseLeave}
      >
        {children}
        {visible && (
          <TipContainer
            position={position}
            backgroundColor={backgroundColor}
            color={color}
            primaryTip={primaryTip}
          >
            {customContent}
            {[].concat(text || []).map(line => <span key={line}>{line}</span>)}
          </TipContainer>
        )}
      </Wrapper>
    );
  }
}

const HelpContainer = styled.div`
  display: inline-block;
  font-weight: normal;
  margin-left: ${p => p.theme.marginHalf};
  position: relative;
  vertical-align: middle;
`;

export const HelpTooltip = props => {
  const { position, text, backgroundColor, primaryTip, customContent } = props;
  const size = props.size || "15";
  return (
    <HelpContainer>
      <Tooltip
        text={text}
        position={position || "bottom"}
        backgroundColor={backgroundColor}
        primaryTip={primaryTip}
        customContent={customContent}
      >
        <HelpIcon height={size} width={size} />
      </Tooltip>
    </HelpContainer>
  );
};
